// @ts-strict-ignore
import React, {
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  AggregationEditorContext,
  AggregationsSubEditorContainer,
} from 'flow/editor/components/aggregations';
import { RuleBuilderContext } from '../../context';
import { ThresholdContext } from './context';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import { resetSubEditor } from 'flow/store/redux/thunks';
import type { AggregationSourceData } from 'flow/editor/components/aggregations/types';
import {
  aggregationComponentsToSourceData,
} from 'flow/editor/components/aggregations/transformers';
import { selectRuleBuilderActiveSubEditor } from 'flow/store/redux/selectors';
import { RuleBuilderSubEditorSide } from 'flow/store/engine/types/utils';
import { AggregationEditorOrigin } from 'flow/editor/components/NodeEditorDrawer/types';
import { getIsPreview } from 'flow/editor/components/NodeEditorDrawer/ThresholdsTab/RuleBuilderContainer/helpers';
import { getMultiSelectProps } from './helpers';

const AggregationsWrapper = () => {
  const dispatch = useAppDispatch();
  const { populatedThreshold } = useContext(ThresholdContext);
  const { keyboardFocus, editMode } = useContext(RuleBuilderContext);
  const activeSubEditor = useAppSelector(selectRuleBuilderActiveSubEditor);

  // Derive origin from the active sub-editor side (LHS or RHS of the threshold)
  const origin = activeSubEditor.side === RuleBuilderSubEditorSide.LHS
    ? AggregationEditorOrigin.THRESHOLD_LEFT
    : AggregationEditorOrigin.THRESHOLD_RIGHT;

  // Read persisted aggregation components from the correct threshold side
  const side = origin === AggregationEditorOrigin.THRESHOLD_LEFT ? 'left' : 'right';
  const attribute = populatedThreshold[side].attribute ?? null;

  const multiSelectProps = getMultiSelectProps({
    action: populatedThreshold[side].action,
    options: {
      isPreview: getIsPreview(editMode, populatedThreshold, activeSubEditor.thresholdId),
    },
  });

  const aggregationEditorContainerRef = useRef<HTMLDivElement>(null);
  const [aggregationContextSourceData, setAggregationContextSourceData] = useState<
  AggregationSourceData
  >(() => aggregationComponentsToSourceData(
    populatedThreshold[side].aggregationComponents,
  ));

  const aggregationEditorContext = useMemo(() => ({
    sourceData: aggregationContextSourceData,
    setSourceData: setAggregationContextSourceData,
    handleClose: () => {
      dispatch(resetSubEditor());
      keyboardFocus.enable();
    },
    aggregationEditorContainerRef,
    origin,
    attribute,
    multiSelectProps,
  }), [
    dispatch,
    keyboardFocus,
    aggregationContextSourceData,
    origin,
    attribute,
    multiSelectProps,
  ]);

  return (
    <AggregationEditorContext.Provider value={aggregationEditorContext}>
      <AggregationsSubEditorContainer data={{ threshold: populatedThreshold }} />
    </AggregationEditorContext.Provider>
  );
};

export default AggregationsWrapper;
